<script setup lang="ts">
import { computed } from 'vue'

import { useCarouselContext } from '@shared/fondof-ui/components/molecules/carousel/useCarouselContext'
import Button from '@storefront-satch/components/atoms/button/Button.vue'
import type { CarouselButtonProps } from '@storefront-satch/components/molecules/carousel/types'
import {
  horizontalCarouselButtonVariants,
  verticalCarouselButtonVariants,
} from '@storefront-satch/components/molecules/carousel/variant'
import { t } from '@storefront-satch/lib/translations'

import { ChevronDownThinIcon } from '@fond-of/satch-icons'

const props = withDefaults(defineProps<CarouselButtonProps>(), {
  size: 'medium',
  position: 'inside',
  variant: 'outlineBlack',
  type: 'next',
})

const {
  canScrollNext,
  canScrollPrev,
  scrollNext,
  scrollPrev,
  prevNextButtons,
  axis,
} = useCarouselContext()

const isPrev = computed(() => props.type === 'prev')
const isNext = computed(() => props.type === 'next')
const isHorizontal = computed(() => axis.value === 'x')
const isVertical = computed(() => axis.value === 'y')

const isDisabled = computed(() => {
  return isPrev.value ? !canScrollPrev.value : !canScrollNext.value
})

const handleClick = () => {
  if (isPrev.value) {
    scrollPrev()
  } else {
    scrollNext()
  }
}
</script>

<template>
  <Button
    v-if="prevNextButtons"
    :class="[
      'bg-white',
      {
        [horizontalCarouselButtonVariants({
          type: props.type,
          position: props.position,
        })]: isHorizontal,
        [verticalCarouselButtonVariants({
          type: props.type,
          position: props.position,
        })]: isVertical,
      },
    ]"
    :variant="props.variant"
    :size="null"
    :padding="null"
    rounded="full"
    :disabled="isDisabled"
    :title="isPrev ? t('carousel.button.prev') : t('carousel.button.next')"
    @click.prevent="handleClick"
  >
    <ChevronDownThinIcon
      class="origin-center"
      :class="{
        'size-8': isHorizontal,
        'size-5': isVertical,
        'mr-1 rotate-90': isHorizontal && isPrev,
        'ml-1 -rotate-90': isHorizontal && isNext,
        'rotate-180': isVertical && isPrev,
        '': isVertical && isNext,
      }"
    />
  </Button>
</template>
